import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Upload, Image, Video, FileText, Loader2, Sparkles, BookOpen, User } from 'lucide-react';
import { IntelligentTeacherSearch } from './IntelligentTeacherSearch';

interface Course {
  id: string;
  title: string;
  description: string;
}

interface AnalysisResult {
  feedback: string;
  recommendedCourses: string[];
  strengths: string[];
  areasForGrowth: string[];
}

export function MediaUploadAnalysis() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');
  const [observedStrengths, setObservedStrengths] = useState('');
  const [areasForGrowth, setAreasForGrowth] = useState('');
  const [overallNarrative, setOverallNarrative] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validFiles = files.filter(file => {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        return isImage || isVideo;
      });
      
      if (validFiles.length !== files.length) {
        toast({
          title: "Some files were skipped",
          description: "Only image and video files are supported",
          variant: "destructive"
        });
      }
      
      setUploadedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const analyzeMedia = async () => {
    if (!selectedTeacher) {
      toast({
        title: "Select a teacher",
        description: "Please select the teacher you observed",
        variant: "destructive"
      });
      return;
    }

    if (uploadedFiles.length === 0 && !observedStrengths.trim() && !areasForGrowth.trim() && !overallNarrative.trim()) {
      toast({
        title: "No content to analyze",
        description: "Please upload media files or add observation notes",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsAnalyzing(true);

      // Load available courses
      const { data: coursesData } = await supabase
        .from('courses')
        .select('id, title, description')
        .eq('status', 'published');

      if (coursesData) {
        setAvailableCourses(coursesData);
      }

      // Upload files to storage
      const fileUrls: string[] = [];
      for (const file of uploadedFiles) {
        const fileName = `walkthrough-${Date.now()}-${file.name}`;
        const { data, error } = await supabase.storage
          .from('practice-videos')
          .upload(fileName, file);

        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('practice-videos')
          .getPublicUrl(fileName);
        
        fileUrls.push(publicUrl);
      }

      // Combine all observation notes for AI analysis
      const combinedNotes = `
OBSERVED STRENGTHS:
${observedStrengths}

AREAS FOR GROWTH:
${areasForGrowth}

OVERALL NARRATIVE:
${overallNarrative}
      `.trim();

      // Call AI analysis edge function
      const { data, error } = await supabase.functions.invoke('analyze-walkthrough-media', {
        body: {
          notes: combinedNotes,
          fileUrls,
          fileTypes: uploadedFiles.map(f => f.type),
          courses: coursesData
        }
      });

      if (error) throw error;

      setAnalysisResult(data);

      toast({
        title: "Analysis complete",
        description: "AI has analyzed your walkthrough observations"
      });

    } catch (error) {
      console.error('Error analyzing media:', error);
      toast({
        title: "Analysis failed",
        description: "Could not analyze the uploaded content",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const assignCourse = async (courseId: string) => {
    if (!selectedTeacher) {
      toast({
        title: "No teacher selected",
        description: "Please select a teacher first",
        variant: "destructive"
      });
      return;
    }

    if (!user) {
      toast({
        title: "Not authenticated",
        description: "Please log in to assign courses",
        variant: "destructive"
      });
      return;
    }

    try {
      // Check if already enrolled
      const { data: existingEnrollment } = await supabase
        .from('enrollments')
        .select('id')
        .eq('course_id', courseId)
        .eq('participant_id', selectedTeacher)
        .maybeSingle();

      if (existingEnrollment) {
        toast({
          title: "Already enrolled",
          description: "This teacher is already enrolled in this course",
          variant: "destructive"
        });
        return;
      }

      // Enroll the teacher
      const { error } = await supabase
        .from('enrollments')
        .insert({
          course_id: courseId,
          participant_id: selectedTeacher,
          enrolled_by: user.id,
          enrollment_type: 'walkthrough_recommendation',
          status: 'enrolled'
        });

      if (error) throw error;

      toast({
        title: "Course assigned",
        description: "The teacher has been enrolled and will be notified"
      });

    } catch (error) {
      console.error('Error assigning course:', error);
      toast({
        title: "Assignment failed",
        description: "Could not assign the course to the teacher",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Teacher Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Select Teacher
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Search and select the teacher you observed
          </p>
        </CardHeader>
        <CardContent>
          <IntelligentTeacherSearch
            onSelectTeacher={setSelectedTeacher}
            selectedTeacherId={selectedTeacher}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Walkthrough Media
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Upload photos, videos, or add notes from your classroom walkthrough for instant AI analysis and coaching recommendations.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File upload area */}
          <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer">
            <input
              type="file"
              id="media-upload"
              multiple
              accept="image/*,video/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <label htmlFor="media-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-2">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Click to upload media</p>
                  <p className="text-sm text-muted-foreground">
                    Images and videos from your device
                  </p>
                </div>
              </div>
            </label>
          </div>

          {/* Uploaded files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Uploaded Files:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="relative p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      {file.type.startsWith('image/') ? (
                        <Image className="h-4 w-4 text-primary" />
                      ) : (
                        <Video className="h-4 w-4 text-primary" />
                      )}
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        className="h-6 w-6 p-0"
                      >
                        ×
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Observation Notes - Three separate fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="observed_strengths" className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Observed Strengths
              </Label>
              <Textarea
                id="observed_strengths"
                value={observedStrengths}
                onChange={(e) => setObservedStrengths(e.target.value)}
                placeholder="Document the teacher's strengths and effective practices you observed..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="areas_for_growth" className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Areas for Growth
              </Label>
              <Textarea
                id="areas_for_growth"
                value={areasForGrowth}
                onChange={(e) => setAreasForGrowth(e.target.value)}
                placeholder="Identify specific areas where the teacher could focus on professional development..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="overall_narrative" className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Overall Narrative
              </Label>
              <Textarea
                id="overall_narrative"
                value={overallNarrative}
                onChange={(e) => setOverallNarrative(e.target.value)}
                placeholder="Provide a comprehensive summary and any additional context about the walkthrough..."
                rows={4}
              />
            </div>
          </div>

          {/* Analyze button */}
          <Button
            onClick={analyzeMedia}
            disabled={!selectedTeacher || isAnalyzing || (uploadedFiles.length === 0 && !observedStrengths.trim() && !areasForGrowth.trim() && !overallNarrative.trim())}
            className="w-full"
            size="lg"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing with AI...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Analyze Walkthrough
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysisResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Analysis Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Overall Feedback */}
            <div>
              <h4 className="font-medium mb-2">Overall Feedback</h4>
              <p className="text-sm text-muted-foreground">{analysisResult.feedback}</p>
            </div>

            {/* Strengths */}
            {analysisResult.strengths.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Observed Strengths</h4>
                <div className="space-y-2">
                  {analysisResult.strengths.map((strength, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Badge variant="secondary" className="mt-0.5">✓</Badge>
                      <p className="text-sm flex-1">{strength}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Areas for Growth */}
            {analysisResult.areasForGrowth.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Areas for Growth</h4>
                <div className="space-y-2">
                  {analysisResult.areasForGrowth.map((area, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Badge variant="outline" className="mt-0.5">→</Badge>
                      <p className="text-sm flex-1">{area}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommended Courses */}
            {analysisResult.recommendedCourses.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Recommended Professional Development
                </h4>
                <div className="space-y-3">
                  {analysisResult.recommendedCourses.map((courseId) => {
                    const course = availableCourses.find(c => c.id === courseId);
                    if (!course) return null;
                    
                    return (
                      <div key={courseId} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h5 className="font-medium">{course.title}</h5>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {course.description}
                            </p>
                          </div>
                          <Button size="sm" onClick={() => assignCourse(courseId)}>
                            Assign
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
